import { SxProps } from '@mui/system';
import { useTheme, Theme } from '@mui/material/styles';
import { ForwardedRef, forwardRef, RefAttributes } from 'react';
import { DataGridPremium, DataGridPremiumProps, GridValidRowModel } from '@mui/x-data-grid-premium';

type ZebraDataGridProps<R extends GridValidRowModel = GridValidRowModel> = DataGridPremiumProps<R>;

const getZebraStyles = (theme: Theme): SxProps<Theme> => {
  const isDarkMode = theme.palette.mode === 'dark';

  const rowBackgroundColor = isDarkMode ? '#121212' : '#fdfdfd';
  const evenRowBackgroundColor = isDarkMode ? '#1a1a1a' : '#f7f7f7';
  const hoverAndSelectRowBackgroundColor = isDarkMode ? '#29303d' : '#eef4fc';

  return {
    border: 'none',

    [`& .row--error`]: {
      color: '#A71111 !important',
    },
    [`& .row--total`]: {
      backgroundColor: isDarkMode ? '#2f2f2f !important' : '#f0f0f0 !important',
      fontWeight: 900,
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: rowBackgroundColor,
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: evenRowBackgroundColor,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: hoverAndSelectRowBackgroundColor,
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: hoverAndSelectRowBackgroundColor,
    },
  };
};

const ZebraDataGrid = forwardRef(function ZebraDataGridInner<R extends GridValidRowModel = GridValidRowModel>(
  props: ZebraDataGridProps<R>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { sx, ...rest } = props;
  const theme = useTheme();

  const zebraStyles = getZebraStyles(theme);

  const mergedSx: SxProps<Theme> = Array.isArray(sx) ? [zebraStyles, ...sx] : sx ? [zebraStyles, sx] : zebraStyles;

  return <DataGridPremium ref={ref} {...rest} sx={mergedSx} />;
}) as <R extends GridValidRowModel = GridValidRowModel>(
  props: ZebraDataGridProps<R> & RefAttributes<HTMLDivElement>
) => JSX.Element;

export default ZebraDataGrid;
